import { createContext, FunctionComponent, RefObject } from "preact";
import { useCallback, useContext, useEffect, useRef, useState } from "preact/hooks";
import { useConfig } from "./ConfigProvider";
import { useWidgetCookie } from "../hooks/widgetCookie";
import { useSendUrlUtmData } from "../hooks/sendUrlUtmData";
import { AgentInfoResponse, getAgentInfo } from "../hooks/agentInfo";
import { z } from "zod";
import goober from "goober";
import { playNotificationSound } from "../utils";

export type WidgetMode = 'menu' | 'chat' | 'voice';

export const POPUP_STATE_KEY = 'sa-widget-popup';

export const getWidgetStateKey = (agentId: string) => `sa-widget-state:${agentId}`;

type PopupState =
  | 'pending' // waiting for the popup delay
  | 'active' // message bubble and badge showing
  | 'hidden' // visitor clicked ×; bubble gone, badge stays
  | 'seen'; // widget was opened; bubble and badge gone for the session

const getStoredPopupState = (): PopupState => {
  try {
    const stored = sessionStorage.getItem(POPUP_STATE_KEY);
    return stored === 'active' || stored === 'hidden' || stored === 'seen' ? stored : 'pending';
  } catch {
    return 'pending';
  }
};

const userUpdateStateSchema = z.object({
  open: z.boolean().optional(),
  mode: z.enum(['menu', 'chat', 'voice']).optional(),
});

type UnifiedWidgetState = {
  open: boolean;
  mode: WidgetMode;
  error: string | null;
}

type UnifiedWidgetStateContext = UnifiedWidgetState & {
  setState: (state: UnifiedWidgetState) => void;
  updateState: (state: Partial<UnifiedWidgetState>) => void;
  setMode: (mode: WidgetMode) => void;
  toggleWidget: () => void;
  closeWidget: () => void;
  initalised: boolean;
  widgetIframe: RefObject<HTMLIFrameElement | null>;
  setError: (error: string | null) => void;
  cookieInitialised: boolean;
  gooberCss: typeof goober.css;
  agentInfo: AgentInfoResponse | null;
  agentInfoError: string | null;
  agentInfoLoaded: boolean;
  showPopupMessage: boolean;
  showPopupBadge: boolean;
  hidePopup: () => void;
}

const UnifiedWidgetStateContext = createContext<UnifiedWidgetStateContext | undefined>(undefined);

/*
 * The unified widget provider manages the state for both chat and voice modes.
 * It orchestrates mode switching and ensures proper initialization.
 */
export const UnifiedWidgetProvider: FunctionComponent<{ gooberCss: typeof goober.css }> = ({ children, gooberCss }) => {
  const { updateConfig, config } = useConfig();
  const { initialised: cookieInitialised } = useWidgetCookie();
  const widgetIframe = useRef<HTMLIFrameElement | null>(null);
  const widgetStateKey = getWidgetStateKey(config.agentId);
  useSendUrlUtmData(widgetIframe);

  // Determine the default mode based on chatOptions config
  const getDefaultMode = useCallback((): WidgetMode => {
    if (config.channels.length > 1) return 'menu';
    if (config.channels.includes('web')) return 'chat';
    if (config.channels.includes('voice')) return 'voice';
    return 'menu'; // default to menu
  }, [config.channels]);

  const getPreviousState = useCallback((): UnifiedWidgetState => {
    try {
      const storedState = localStorage.getItem(widgetStateKey);
      if (storedState) {
        const { savedAt, ...previousState } = JSON.parse(storedState);
        if (config.stateExpiry === null || savedAt > Date.now() - config.stateExpiry * 60_000) {
          return previousState;
        }
        localStorage.removeItem(widgetStateKey);
      }
    } catch {}
    return {
      open: config.autoOpen,
      mode: getDefaultMode(),
      error: null,
    }
  }, [getDefaultMode, widgetStateKey, config.stateExpiry])

  const [state, setState] = useState<UnifiedWidgetState>(getPreviousState());
  const [agentInfo, setAgentInfo] = useState<AgentInfoResponse | null>(null);
  const [agentInfoError, setAgentInfoError] = useState<string | null>(null);
  const [agentInfoLoaded, setAgentInfoLoaded] = useState(false);
  const [popupState, setPopupStateValue] = useState<PopupState>(getStoredPopupState);

  const needsAgentInfo = config.channels.includes('voice') || config.channels.includes('whatsapp') || !!config.popupMessage;

  useEffect(() => {
    if (!config.agentId || !needsAgentInfo) return;

    const apiUrl = process.env.WEBSITE_DOMAIN || 'https://localhost:8014';
    getAgentInfo(config.agentId, apiUrl)
      .then(setAgentInfo)
      .catch((error) => {
        console.error('Failed to load agent info:', error);
        setAgentInfoError(error instanceof Error ? error.message : 'Failed to load agent configuration');
      })
      .finally(() => setAgentInfoLoaded(true));
  }, [config.agentId, needsAgentInfo]);

  // Always show the widget button - cookie init only needed for chat mode
  const initalised = true;

  const saveState = useCallback((stateToSave: Partial<UnifiedWidgetState>) => {
    try {
      localStorage.setItem(widgetStateKey, JSON.stringify({ ...stateToSave, savedAt: Date.now() }));
    } catch {}
  }, [widgetStateKey]);

  const refreshSavedAt = useCallback(() => {
    try {
      const storedState = localStorage.getItem(widgetStateKey);
      if (storedState) {
        localStorage.setItem(widgetStateKey, JSON.stringify({ ...JSON.parse(storedState), savedAt: Date.now() }));
      }
    } catch {}
  }, [widgetStateKey]);

  useEffect(() => {
    if (config.stateExpiry === null) return;
    refreshSavedAt();
    const interval = setInterval(refreshSavedAt, 60_000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') refreshSavedAt();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', refreshSavedAt);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', refreshSavedAt);
    };
  }, [refreshSavedAt, config.stateExpiry]);

  const updateState = useCallback((newState: Partial<UnifiedWidgetState>) => {
    setState((prevState) => {
      const nextState = { ...prevState, ...newState };
      saveState(nextState);
      return nextState;
    });
  }, [setState, saveState]);


  const userUpdateState = useCallback((newState: any) => {
    try {
      const updatedState = userUpdateStateSchema.parse(newState);
      updateState(updatedState);
    } catch (error) {
      console.error('Invalid state update', error);
    }
  }, [updateState]);

  const setMode = useCallback((mode: WidgetMode) => {
    updateState({ mode, open: true });
  }, [updateState]);

  const toggleWidget = useCallback(() => {
    setState((prevState) => {
      const opening = !prevState.open;
      const nextState = {
        ...prevState,
        open: opening,
        // When opening, go to default mode based on chatOptions
        mode: opening ? getDefaultMode() : prevState.mode,
      };
      saveState(nextState);
      return nextState;
    });
  }, [getDefaultMode, saveState]);

  const closeWidget = useCallback(() => {
    setState((prevState) => {
      const nextState = {
        ...prevState,
        open: false,
        mode: getDefaultMode(),
      };
      saveState(nextState);
      return nextState;
    });
  }, [getDefaultMode, saveState]);

  const setPopupState = useCallback((popupState: PopupState) => {
    try {
      sessionStorage.setItem(POPUP_STATE_KEY, popupState);
    } catch {}
    setPopupStateValue(popupState);
  }, []);

  const hidePopup = useCallback(() => {
    setPopupState('hidden');
  }, [setPopupState]);

  useEffect(() => {
    if (state.open && popupState !== 'seen') setPopupState('seen');
  }, [state.open, popupState, setPopupState]);

  useEffect(() => {
    if (!config.popupMessage || popupState !== 'pending' || state.open) return;
    const timer = setTimeout(() => {
      setPopupState('active');
      if (config.popupSound) playNotificationSound();
    }, config.popupDelay * 1000);
    return () => clearTimeout(timer);
  }, [config.popupMessage, config.popupDelay, config.popupSound, popupState, state.open, setPopupState]);

  const setError = useCallback((error: string | null) => {
    updateState({ error });
  }, [updateState]);

  useEffect(() => {
    // Create a SAWidget object for interfacing with the widget
    const existingWidget = window.SAWidget || { init: () => {} };
    window.SAWidget = {
      ...existingWidget,
      setConfig: updateConfig,
      updateState: userUpdateState,
      clearState: () => {
        try {
          localStorage.removeItem(widgetStateKey);
          sessionStorage.removeItem(POPUP_STATE_KEY);
        } catch {}
      },
    };
  }, [userUpdateState, widgetStateKey]);

  return (
    <UnifiedWidgetStateContext.Provider value={{
      ...state,
      setState,
      updateState,
      setMode,
      toggleWidget,
      closeWidget,
      initalised,
      widgetIframe,
      setError,
      cookieInitialised, // Pass this separately for chat mode to check
      gooberCss,
      agentInfo,
      agentInfoError,
      agentInfoLoaded,
      showPopupMessage: popupState === 'active',
      showPopupBadge: popupState === 'active' || popupState === 'hidden',
      hidePopup,
    }}>
      {children}
    </UnifiedWidgetStateContext.Provider>
  )
}

export const useUnifiedWidget = (): UnifiedWidgetStateContext => {
  const context = useContext(UnifiedWidgetStateContext);
  if (!context) {
    throw new Error('useUnifiedWidget must be used within a UnifiedWidgetProvider');
  }
  return context;
};

